/**
 * ContractList page — searchable, sortable, filterable DataTable of contracts.
 *
 * Requirements: 3.1, 3.7, 27.7, 27.8
 */

import { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { StatusBadge, type BadgeVariant } from '@/components/ui/StatusBadge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { contractService, type ContractListParams } from '@/services/contractService';
import type { Contract, SortDirection } from '@/types';
import { useAuth } from '@/contexts/AuthContext';

const LIFECYCLE_STATUS_VARIANTS: Record<string, BadgeVariant> = {
  Draft: 'gray',
  'Under Review': 'blue',
  Active: 'green',
  Expiring: 'yellow',
  Expired: 'red',
  Renewed: 'blue',
  Terminated: 'red',
  Archived: 'gray',
};

const LIFECYCLE_STATUSES = [
  'Draft',
  'Under Review',
  'Active',
  'Expiring',
  'Expired',
  'Renewed',
  'Terminated',
  'Archived',
] as const;

export function ContractList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = user?.isAdmin ||
    user?.roles.some((r) =>
      ['Admin', 'Contract Specialist', 'Facilities Admin'].includes(r),
    );

  const [params, setParams] = useState<ContractListParams>({
    page: 1,
    pageSize: 25,
    sortBy: 'contractTitle',
    sortDir: 'asc',
  });

  const [statusFilter, setStatusFilter] = useState<string>('');
  const [glCodeFilter, setGlCodeFilter] = useState<string>('');
  const [expirationFrom, setExpirationFrom] = useState<string>('');
  const [expirationTo, setExpirationTo] = useState<string>('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['contracts', params, statusFilter, glCodeFilter, expirationFrom, expirationTo],
    queryFn: () =>
      contractService.list({
        ...params,
        lifecycleStatus: statusFilter || undefined,
        glCode: glCodeFilter || undefined,
        expirationDateFrom: expirationFrom || undefined,
        expirationDateTo: expirationTo || undefined,
      }),
  });

  const handleSearch = useCallback((search: string) => {
    setParams((prev) => ({ ...prev, search, page: 1 }));
  }, []);

  const handleSort = useCallback((key: string, direction: SortDirection) => {
    setParams((prev) => ({ ...prev, sortBy: key, sortDir: direction }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setParams((prev) => ({ ...prev, page }));
  }, []);

  const columns: Column<Contract & Record<string, unknown>>[] = [
    {
      key: 'contractTitle',
      header: 'Contract Title',
      sortable: true,
      render: (row) => (
        <Link
          to={`/contracts/${row.contractId}`}
          className="text-baker-red no-underline font-medium hover:underline"
        >
          {row.contractTitle}
        </Link>
      ),
    },
    {
      key: 'lifecycleStatus',
      header: 'Status',
      sortable: true,
      width: '130px',
      render: (row) => (
        <StatusBadge
          label={row.lifecycleStatus}
          variant={LIFECYCLE_STATUS_VARIANTS[row.lifecycleStatus] ?? 'gray'}
        />
      ),
    },
    {
      key: 'contractType',
      header: 'Type',
      sortable: false,
      width: '120px',
      render: (row) => row.contractType ?? '—',
    },
    {
      key: 'departmentScope',
      header: 'Department',
      sortable: true,
      width: '120px',
    },
    {
      key: 'glCode',
      header: 'GL Code',
      sortable: true,
      width: '100px',
      render: (row) => row.glCode ?? '—',
    },
    {
      key: 'expirationDate',
      header: 'Expiration',
      sortable: true,
      width: '120px',
      render: (row) => {
        if (!row.expirationDate) return '—';
        const date = new Date(row.expirationDate);
        const now = new Date();
        const daysUntil = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        const isExpiringSoon = daysUntil >= 0 && daysUntil <= 30;
        return (
          <span className={isExpiringSoon ? 'text-red-600 font-semibold' : 'text-slate-700'}>
            {date.toLocaleDateString()}
          </span>
        );
      },
    },
    {
      key: 'contractValue',
      header: 'Value',
      sortable: true,
      width: '120px',
      render: (row) =>
        row.contractValue != null
          ? `${Number(row.contractValue).toLocaleString()}`
          : '—',
    },
    {
      key: 'updatedAt',
      header: 'Updated',
      sortable: true,
      width: '110px',
      render: (row) =>
        row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : '—',
    },
  ];

  return (
    <div>
      {/* Page header */}
      <div className="flex justify-between items-center mb-5">
        <h1 className="m-0 text-lg font-semibold text-text-primary">Contracts</h1>
        {canWrite && (
          <button
            onClick={() => navigate('/contracts/new')}
            className="px-4 py-2 rounded-lg border-none bg-baker-red text-white cursor-pointer text-sm font-medium hover:bg-baker-red/90 transition-all"
          >
            + New Contract
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-5 flex-wrap items-end">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setParams((prev) => ({ ...prev, page: 1 }));
            }}
            className="py-2 px-3 rounded-lg border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-baker-accent/40 focus:border-baker-accent transition-all"
          >
            <option value="">All</option>
            {LIFECYCLE_STATUSES.map((status) => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">GL Code</label>
          <input
            type="text"
            value={glCodeFilter}
            onChange={(e) => {
              setGlCodeFilter(e.target.value);
              setParams((prev) => ({ ...prev, page: 1 }));
            }}
            placeholder="e.g., 100-2000"
            className="py-2 px-3 rounded-lg border border-border text-sm w-28 bg-white focus:outline-none focus:ring-2 focus:ring-baker-accent/40 focus:border-baker-accent transition-all placeholder:text-slate-400"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Expires from</label>
          <input
            type="date"
            value={expirationFrom}
            onChange={(e) => {
              setExpirationFrom(e.target.value);
              setParams((prev) => ({ ...prev, page: 1 }));
            }}
            className="py-2 px-3 rounded-lg border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-baker-accent/40 focus:border-baker-accent transition-all"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">to</label>
          <input
            type="date"
            value={expirationTo}
            onChange={(e) => {
              setExpirationTo(e.target.value);
              setParams((prev) => ({ ...prev, page: 1 }));
            }}
            className="py-2 px-3 rounded-lg border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-baker-accent/40 focus:border-baker-accent transition-all"
          />
        </div>
      </div>

      {error ? (
        <ErrorMessage
          message={(error as Error).message ?? 'Failed to load contracts.'}
          onRetry={() => refetch()}
        />
      ) : isLoading ? (
        <LoadingSpinner message="Loading contracts…" />
      ) : (
        <DataTable
          columns={columns}
          data={(data?.data ?? []) as (Contract & Record<string, unknown>)[]}
          rowKey={(row) => row.contractId}
          totalItems={data?.total}
          page={params.page}
          pageSize={params.pageSize}
          onPageChange={handlePageChange}
          onSort={handleSort}
          onSearch={handleSearch}
          searchPlaceholder="Search contracts…"
          emptyMessage="No contracts found."
        />
      )}
    </div>
  );
}
